import { useState } from 'react'
import {
  Check,
  Zap,
  Target,
  Shield,
  Globe,
  BarChart3,
  MessageSquare,
  Brain,
  ArrowRight,
  Users,
  User,
  Briefcase,
  Megaphone,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Deck Final (/deck-final)
   Copy of deck3 for post-call edits. Same content; plan edits applied here.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function DeckFinalPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="deck-final-login fixed inset-0 flex items-center justify-center p-4 sm:p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="deck-final-input w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="deck-final-input w-full rounded-lg bg-white px-4 py-3 text-base font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white deck-print-root pb-0 mb-0" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-4 right-4 sm:top-6 sm:right-8 z-50">
        <span className="text-[9px] sm:text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>
      <div className="deck-container w-full max-w-[1920px] mx-auto mb-0 pb-0">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className="deck-slide w-full flex flex-col border-b border-white/[0.04] min-h-[100dvh] sm:min-h-0 sm:h-[1080px]"
          >
            <div className="slide-inner flex-1 flex flex-col justify-center w-full min-w-0 px-4 py-8 sm:px-8 sm:py-12 md:px-12 md:py-16 lg:px-20 lg:py-20 xl:px-24 xl:py-20">
              <Section />
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & SHARED
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide { title: string; component: React.FC }

function SlideTag({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest ${className}`}>
      {children}
    </span>
  )
}

/** DIZEE brand name (no letter-spacing per request) */
function DizeeWord({ className = '' }: { className?: string }) {
  return <span className={className || undefined}>DIZEE</span>
}

function TwoCol({
  tag,
  tagNode,
  headline,
  headlineNode,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  tagNode?: React.ReactNode
  headline?: string
  headlineNode?: React.ReactNode
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        {(tagNode ?? tag) && <SlideTag>{tagNode ?? tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          {headlineNode ?? (<>{(headline ?? '')}{highlight && <span className="text-[#FF2300]">{highlight}</span>}</>)}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">{subline}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] min-w-0">
        {right}
      </div>
    </div>
  )
}

function GrayBox({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-zinc-800/40 p-5 min-w-0">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[14px] sm:text-base text-zinc-400 leading-relaxed break-words">{children}</p>
    </div>
  )
}

/* Problem slide & Solution slide: same deck logos, floating with label underneath. */
const PROBLEM_LOGOS: { label: string; src: string }[] = [
  { label: 'iMessage', src: '/logos/deck/apple.svg' },
  { label: 'Asana', src: '/logos/deck/asana.svg' },
  { label: 'ASCAP', src: '/logos/deck/ascap.svg' },
  { label: 'Gmail', src: '/logos/deck/gmail.svg' },
  { label: 'Google Sheets', src: '/logos/deck/googlesheets.svg' },
  { label: 'MasterTour', src: '/logos/deck/mastertour.svg' },
  { label: 'Slack', src: '/logos/deck/slack.svg' },
  { label: 'Spotify', src: '/logos/deck/spotify.svg' },
]

function MockFragmentedLogos() {
  const items: {
    left: string
    top: string
    rotation?: number
    opacity?: number
    translateX?: number
    translateY?: number
    zIndex?: number
  }[] = [
    { left: '24%', top: '6%', rotation: -5, opacity: 0.94, translateX: 4, translateY: -2, zIndex: 10 },
    { left: '68%', top: '8%', rotation: 4, opacity: 1, translateX: -3, translateY: 5, zIndex: 8 },
    { left: '75%', top: '38%', rotation: -2, opacity: 0.9, translateX: 6, translateY: 2, zIndex: 12 },
    { left: '22%', top: '32%', rotation: 3, opacity: 0.97, translateX: -5, translateY: -4, zIndex: 9 },
    { left: '44%', top: '26%', rotation: -4, opacity: 0.92, translateX: 2, translateY: 6, zIndex: 11 },
    { left: '14%', top: '58%', rotation: 2, opacity: 0.98, translateX: -2, translateY: -3, zIndex: 13 },
    { left: '48%', top: '62%', rotation: -3, opacity: 0.93, translateX: 5, translateY: 1, zIndex: 7 },
    { left: '72%', top: '68%', rotation: 5, opacity: 0.96, translateX: -4, translateY: 4, zIndex: 14 },
  ]
  return (
    <div className="relative w-full max-w-lg h-[280px] sm:h-[320px] md:h-[380px]">
      {PROBLEM_LOGOS.map((logo, i) => {
        const item = items[i]
        const tx = item.translateX ?? 0
        const ty = item.translateY ?? 0
        return (
          <div
            key={i}
            className="absolute flex flex-col items-center gap-2"
            style={{
              left: item.left,
              top: item.top,
              zIndex: item.zIndex ?? 10,
              opacity: item.opacity ?? 1,
              transform: `translate(${tx}px, ${ty}px) rotate(${item.rotation ?? 0}deg)`,
            }}
          >
            <img src={logo.src} alt={logo.label} className="h-8 w-auto object-contain sm:h-12" />
            <span className="text-[10px] sm:text-[12px] text-zinc-500 whitespace-nowrap">{logo.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/* Solution slide: tools + people (artist, agent, promoter, managers) all connecting to center. */
const SOLUTION_ITEMS: (
  | { type: 'logo'; label: string; src: string }
  | { type: 'role'; label: string; Icon: React.ComponentType<{ className?: string }> }
)[] = [
  ...PROBLEM_LOGOS.map((l) => ({ type: 'logo' as const, label: l.label, src: l.src })),
  { type: 'role', label: 'Artist', Icon: User },
  { type: 'role', label: 'Agent', Icon: Briefcase },
  { type: 'role', label: 'Promoter', Icon: Megaphone },
  { type: 'role', label: 'Managers', Icon: Users },
]

function MockCentralDizzy() {
  // 12 items on a circle: center 50%,50%, radius 38% — even angles, no overlap
  const radius = 38
  const positions = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
    const rad = (deg * Math.PI) / 180
    const x = 50 + radius * Math.sin(rad)
    const y = 50 - radius * Math.cos(rad)
    return { x, y, left: `${x.toFixed(1)}%`, top: `${y.toFixed(1)}%` }
  })

  return (
    <div className="relative w-full max-w-lg flex flex-col items-center">
      <div className="relative w-full aspect-square max-h-[240px] sm:max-h-[280px] md:max-h-[320px] mx-auto">
        {SOLUTION_ITEMS.map((item, i) => (
          <div
            key={i}
            className="absolute flex flex-col items-center justify-center gap-1.5 z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: positions[i].left, top: positions[i].top }}
          >
            {item.type === 'logo' ? (
              <img src={item.src} alt={item.label} className="h-7 w-auto object-contain sm:h-10" />
            ) : (
              <div className="h-7 w-7 sm:h-10 sm:w-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-zinc-400 shrink-0">
                <item.Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </div>
            )}
            <span className="text-[9px] sm:text-[11px] text-zinc-500 whitespace-nowrap">{item.label}</span>
          </div>
        ))}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {positions.map((pos, i) => (
            <line
              key={i}
              x1={pos.x}
              y1={pos.y}
              x2={50}
              y2={50}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="0.35"
              strokeDasharray="1.5 1"
            />
          ))}
        </svg>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-1.5 z-10">
          <div className="w-7 h-7 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-5 w-auto text-white" />
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="mt-6 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The <span className="text-[#FF2300]">new operating system</span> and{' '}
        <span className="text-[#FF2300]">source of truth</span>
        {' '}for modern music careers.
      </p>
      <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-base sm:text-[17px] text-white">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-2 text-[14px] sm:text-[15px] font-medium text-[#FF2300]">Pre-seed</span>
        <span>February 2026</span>
        <span>dizee.com</span>
      </div>
    </div>
  )
}

function SlideMission() {
  return (
    <div className="flex flex-col justify-center">
      <SlideTag>Mission</SlideTag>
      <h2 className="mt-8 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white leading-[1.25] max-w-4xl">
        <span className="block whitespace-normal sm:whitespace-nowrap">&quot;To unify every part of a music career within <span className="text-[#FF2300]">one intelligent system</span>,</span>
        <span className="block"><span className="whitespace-normal sm:whitespace-nowrap">enabling artists and their teams to move faster, make smarter decisions</span>,</span>
        <span className="block">and stay aligned around <span className="text-[#FF2300] whitespace-normal sm:whitespace-nowrap">a single source of truth.</span>&quot;</span>
      </h2>
    </div>
  )
}

function SlideWhatsHappening() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        <SlideTag>What&apos;s Happening Now</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          The music industry has <span className="text-[#FF2300] font-bold">shifted.</span>
        </h2>
        <p className="mt-2 text-lg text-zinc-400">Music is a multi-billion dollar global industry now run by independent teams.</p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4 min-w-0">
          <GrayBox title={<span className="text-[#FF2300]">New landscape</span>}>The music industry is undergoing a seismic technological and cultural transformation.</GrayBox>
          <GrayBox title={<span className="text-[#FF2300]">Decentralized Power</span>}>Labels and traditional gatekeepers no longer manage most careers.</GrayBox>
          <GrayBox title={<span className="text-[#FF2300]">Independent Era</span>}>Independent artists, managers, and small teams now run their own businesses.</GrayBox>
          <GrayBox title={<span className="text-[#FF2300]">Infrastructure Gap</span>}>Responsibility moved to artists and teams, but the operating infrastructure never followed.</GrayBox>
        </div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[360px] w-full">
        <div className="relative w-full max-w-lg rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
          <img
            src="/deck/industry-wins.png"
            alt="Creators on top — independent power rising"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  )
}

function SlideIndustryTrends() {
  return (
    <div>
      <SlideTag>Industry Trends</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
        Here&apos;s what&apos;s coming next.
      </h2>
      <p className="mt-2 text-lg text-zinc-400">The Rise of Independent Artists, Creators and Teams operating their own small businesses.</p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
        <GrayBox title={<span className="text-[#FF2300]">Barrier to Entry</span>}>The barrier to entry for music creators has dramatically decreased.</GrayBox>
        <GrayBox title={<span className="text-[#FF2300]">Rise of the Creators</span>}>Adjacent markets like the creator economy are expanding rapidly. Creator Economy: $252.33B (2025).</GrayBox>
        <GrayBox title={<span className="text-[#FF2300]">Fragmented Ecosystem</span>}>The gap between how creators operate and the tools supporting them continues to widen.</GrayBox>
        <GrayBox title={<span className="text-[#FF2300]">Gen AI Tools Accelerating</span>}>AI and new creation tools are accelerating the number of new music makers.</GrayBox>
      </div>
      <div className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-6 sm:gap-12">
        <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="rounded-xl border border-[#FF2300]/25 bg-[#FF2300]/[0.08] px-8 py-5 hover:bg-[#FF2300]/[0.12] transition-colors text-center min-w-0 sm:min-w-[280px] sm:flex-1 sm:max-w-[360px]">
          <p className="text-4xl sm:text-5xl font-bold text-[#FF2300]">~75M</p>
          <p className="text-[14px] text-zinc-400 mt-1">Active music creators worldwide</p>
          <p className="text-[11px] text-zinc-600 mt-1">Source</p>
        </a>
        <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08] px-8 py-5 hover:bg-emerald-500/[0.12] transition-colors text-center min-w-0 sm:min-w-[280px] sm:flex-1 sm:max-w-[360px]">
          <p className="text-4xl sm:text-5xl font-bold text-emerald-400">200M+</p>
          <p className="text-[14px] text-zinc-400 mt-1">200M+ expected by 2030</p>
          <p className="text-[11px] text-zinc-600 mt-1">Source</p>
        </a>
      </div>
      <p className="mt-10 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-zinc-300 italic text-center leading-snug">
        The industry has changed. The tools haven&apos;t.
      </p>
    </div>
  )
}

function SlideProblemCurrentTools() {
  return (
    <TwoCol
      tag="Current State"
      headline="The music business is still "
      highlight="outdated."
      subline="No unified system to manage the modern music business."
      right={<MockFragmentedLogos />}
    >
      <div className="mt-10 grid sm:grid-cols-2 gap-5 min-w-0">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Manual Processes</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Nothing automated or connected.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Fragmented Communication</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Spreadsheets, texts, email — critical decisions in iMessage nobody can search.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Legacy Tools</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Built for a different era. Can&apos;t keep up.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Disconnected Systems</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Data in a dozen platforms. No way to connect the dots.</p>
        </div>
      </div>
    </TwoCol>
  )
}

function SlideProblemHighLevel() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] w-full order-2 lg:order-1">
        <div className="relative w-full max-w-lg rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10] bg-white/[0.03]">
          <img
            src="/deck/musicwork.png"
            alt="Music work"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
      <div className="min-w-0 order-1 lg:order-2">
        <SlideTag>The Problem</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.15]">
          Artists lack clear <span className="text-[#FF2300] whitespace-normal sm:whitespace-nowrap">real-time visibility</span> into what&apos;s happening in their career.
        </h2>
        <div className="mt-10 max-w-3xl space-y-5">
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">No Centralized System:</p>
            <p className="text-base text-zinc-300 leading-relaxed">Fragmented tools and data force music teams to make critical revenue decisions without the full picture.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">Limited Access to Fast, Reliable Data:</p>
            <p className="text-base text-zinc-300 leading-relaxed">Artist teams lack timely, reliable access to the info needed to act quickly, prevent risks or capture valuable opportunities.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">Missed Opportunities:</p>
            <p className="text-base text-zinc-300 leading-relaxed">By the time data is gathered and verified, opportunities are often missed and communication breaks down.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-2">This Results In:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-base text-zinc-300 leading-relaxed">
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Lost Revenue</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Slowed Growth</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Missed Opportunities</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Communication Breakdowns</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideExampleMessages() {
  return (
    <div>
      <SlideTag>Real Conversations</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tighter text-white">
        Before vs after <DizeeWord className="text-[#FF2300]" />.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-none">
        <div className="min-h-[320px] w-full">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Before <DizeeWord className="text-[#FF2300]" /> — messages scattered</p>
          <img src="/deck/conversations.png" alt="Before and after DIZEE conversations" className="w-full h-auto object-contain" />
        </div>
        <div className="space-y-4">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">After <DizeeWord className="text-[#FF2300]" /></p>
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 space-y-4">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ticket count</p>
              <p className="text-xl font-semibold text-white">New York: Brooklyn Storehouse</p>
              <p className="text-sm text-zinc-400 mt-0.5">5,000 capacity, 2,435 sold, 48.7% sold</p>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300] w-[48.7%]" />
              </div>
              <p className="text-[12px] text-[#FF2300] mt-1">48.7% sold — updated live</p>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Schedule</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                  <span className="text-[12px] text-white">Brooklyn Storehouse NY</span>
                </div>
                <div className="flex items-center gap-2 text-base text-zinc-300">
                  <span>LA → Chicago → NY</span>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Music · Streams</p>
              <div className="flex items-center gap-2">
                <img src="/deck/onmymind.png" alt="On My Mind" className="w-10 h-10 rounded object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="text-base text-white font-medium">On My Mind</p>
                  <p className="text-base text-zinc-500">Diplo, Sidepiece · 334M streams</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideCentralizes() {
  return (
    <>
    <TwoCol
      tag="The Solution"
      headlineNode={
        <span className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.15]">
          <DizeeWord className="text-[#FF2300]" />
          {' '}centralizes the modern music career.
        </span>
      }
      subline={undefined}
      right={
        <div className="flex flex-col items-center w-full">
          <MockCentralDizzy />
          <p className="mt-10 text-center text-sm text-zinc-500">Many tools → one system.</p>
        </div>
      }
    >
      <>
        <p className="mt-4 text-base sm:text-lg text-zinc-400">
          Into a single{' '}
          <span className="text-[#FF2300] font-semibold">operating system</span>
          {' '}and{' '}
          <span className="text-[#FF2300] font-semibold">source of truth.</span>
        </p>
        <hr className="mt-4 border-white/10" />
      </>
      <div className="mt-8 grid sm:grid-cols-1 gap-4">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <BarChart3 className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Real-Time Clarity</h3>
          <p className="text-base text-zinc-400">Unified view of releases, shows, finances, team — in one place.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Brain className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Smarter Decisions</h3>
          <p className="text-base text-zinc-400">AI surfaces opportunities as they happen so you can act, not react.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Target className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">End-to-End Management</h3>
          <p className="text-base text-zinc-400">Catalog, touring, team, projects — all connected.</p>
        </div>
      </div>
    </TwoCol>
    <p className="mt-10 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-zinc-300 italic text-center leading-snug">
      Anyone can now manage a modern music career end-to-end with{' '}
      <DizeeWord className="text-white" />
    </p>
    </>
  )
}

const PRODUCT_ITEMS: { title: string; desc: string; mock: React.ReactNode }[] = [
  {
    title: 'Dashboard',
    desc: 'Real-time view of your entire music career in one place',
    mock: (
      <div className="w-full text-left p-2 space-y-1.5 min-h-[7.5rem] flex flex-col justify-center">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span className="text-[#FF2300] font-medium">Shows</span><span>Schedule</span><span>Tasks</span></div>
        <div className="grid grid-cols-2 gap-1"><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">42</span><span className="text-[10px] block text-zinc-500">Shows</span></div><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">53</span><span className="text-[10px] block text-zinc-500">Tasks</span></div></div>
      </div>
    ),
  },
  {
    title: 'Teams',
    desc: 'Connect team members and streamline communication',
    mock: (
      <div className="w-full text-left p-2 space-y-1 min-h-[7.5rem] flex flex-col justify-center">
        {[{ name: 'Artist', initials: 'A' }, { name: 'Promoter', initials: 'P' }, { name: 'Agent', initials: 'Ag' }].map((m, i) => (
          <div key={i} className="flex items-center gap-2 py-1 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2">
            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <span className="text-[9px] font-semibold text-zinc-400">{m.initials}</span>
            </div>
            <span className="text-[11px] text-zinc-400">{m.name}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'Shows',
    desc: 'Ticket counts, guarantee tracking, promoter CRM',
    mock: (
      <div className="w-full text-left p-2 min-h-[7.5rem] flex flex-col justify-center space-y-1">
        <div className="text-[11px] text-zinc-500">Tempo · Cap 400 · Sold 328</div>
        <div className="h-1.5 rounded-full bg-white/10 backdrop-blur-sm"><div className="h-full rounded-full bg-[#FF2300] w-[82%]" /></div>
        <div className="flex justify-between text-[10px]"><span className="text-[#FF2300]">82%</span><span className="text-zinc-500">$8,200 / $10k cap</span></div>
      </div>
    ),
  },
  {
    title: 'Music',
    desc: 'Discography, metadata, splits, and credits.',
    mock: (
      <div className="w-full h-full min-h-0 text-left p-2 space-y-1.5 flex flex-col justify-center">
        <div className="text-[10px] text-zinc-500 border-b border-white/10 pb-1">Releases · Streams</div>
        <div className="flex items-center gap-2">
          <img src="/deck/onmymind.png" alt="On My Mind" className="w-8 h-8 rounded object-cover shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] text-white font-medium truncate">On My Mind</p>
            <p className="text-[10px] text-zinc-500">Diplo, Sidepiece · 334M streams</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'Schedule',
    desc: 'Calendar, events, routing, and travel. Keep the whole team in sync.',
    mock: (
      <div className="w-full h-full min-h-0 text-left p-2 space-y-1 flex flex-col justify-center">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span>Routing</span><span>Travel</span><span>Crew</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 space-y-0.5">
          <p className="text-[11px] text-zinc-400">Denver → LA → Chicago</p>
          <p className="text-[10px] text-zinc-500">Apr 2–8 · Confirmed</p>
        </div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Crew ETA 4:30 PM</span></div>
      </div>
    ),
  },
  {
    title: 'Tasks',
    desc: 'Tasks, notes, files, and collaboration. Everything in one place.',
    mock: (
      <div className="w-full h-full min-h-0 text-left p-2.5 space-y-1.5 flex flex-col justify-center">
        <div className="space-y-0.5">
          <p className="flex gap-2 text-[10px] text-zinc-500"><span className="text-[#FF2300] font-medium">3</span> due today</p>
          <p className="text-[10px] text-zinc-500">Notes and files in one place</p>
        </div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" /><span className="text-[11px] text-zinc-400">Finalize splits</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-zinc-500" /><span className="text-[11px] text-zinc-400">Send artwork</span></div>
      </div>
    ),
  },
  {
    title: 'AI Intelligence',
    desc: 'At-risk shows, pacing vs last time, bonuses. Factual insights in context.',
    mock: (
      <div className="w-full text-left p-3 space-y-2 min-h-[7.5rem] flex flex-col justify-center">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium border-b border-white/10 pb-1.5">Insights</div>
        <div className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2.5 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="text-[11px] text-zinc-300">LA pacing 20% slower than last LA</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-zinc-500">990 sold</span>
            <span className="text-[#FF2300] font-medium">$2K bonus at 1,000</span>
          </div>
          <div className="h-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#FF2300]/80 w-[99%]" /></div>
        </div>
      </div>
    ),
  },
  {
    title: '24/7 Sync',
    desc: 'Google Sheets, email, platform integrations',
    mock: (
      <div className="w-full text-left p-3 space-y-2 min-h-[7.5rem] flex flex-col justify-center">
        <div className="space-y-1.5">
          {[
            { label: 'Google Sheets', status: 'Live', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Email', status: 'Synced', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Last sync', status: '2m ago', color: 'bg-white/5 border-white/10 text-zinc-500' },
          ].map((row, i) => (
            <div key={i} className={`rounded-lg border px-2 py-1.5 flex items-center justify-between text-[11px] backdrop-blur-sm ${row.color}`}>
              <span className="text-zinc-300">{row.label}</span>
              <span className="font-medium">{row.status}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
]
function SlideProduct() {
  return (
    <div className="flex flex-col">
      <SlideTag>Product</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        A unified platform that centralizes your career.
      </h2>
      <p className="mt-2 text-zinc-400 text-base">Dashboard, Teams, Shows, Music, Schedule, Tasks, AI Intelligence, 24/7 Sync.</p>

      {/* Hero: phone (left) + laptop/overview — billboard-style, phone bottom-aligned with laptop */}
      <div className="mt-10 lg:mt-12 flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-4 lg:gap-5 w-full pb-10 lg:pb-10">
        <div className="relative flex flex-col items-center order-2 lg:order-1 lg:flex-initial lg:min-h-0">
          <div className="hidden lg:flex flex-1 min-h-0 flex-col justify-end">
            <img
              src="/deck/phone-mock.png"
              alt="DIZEE mobile — Schedule, always in sync"
              className="w-auto h-[320px] object-contain drop-shadow-2xl"
            />
          </div>
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <img
              src="/deck/phone-mock.png"
              alt="DIZEE mobile — Schedule, always in sync"
              className="w-auto h-[320px] object-contain drop-shadow-2xl"
            />
            <p className="text-sm text-zinc-500">Mobile · Always in sync</p>
          </div>
          <p className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full mt-2 text-sm text-zinc-500 whitespace-nowrap">Mobile · Always in sync</p>
        </div>
        <div className="w-full max-w-[85%] lg:max-w-[55%] flex justify-center order-1 lg:order-2">
          <img
            src="/deck/dizee-overview.png"
            alt="DIZEE overview — dashboard, schedule, shows, tasks, streams"
            className="w-full h-auto object-contain drop-shadow-2xl"
          />
        </div>
      </div>
    </div>
  )
}

function SlideProductBreakdown() {
  return (
    <div>
      <SlideTag>Product</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        Product breakdown.
      </h2>
      <p className="mt-2 text-zinc-500 text-base max-w-2xl">One platform. Every part of your career.</p>

      <hr className="mt-8 border-white/[0.08]" />

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {PRODUCT_ITEMS.map((item, i) => (
          <div key={i} className="group flex flex-col gap-3 min-h-[260px]">
            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-widest">{item.title}</span>
            <p className="text-[15px] text-zinc-300 leading-snug line-clamp-2">{item.desc}</p>
            <div className="mt-2 rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 h-[10.5rem] min-h-[10.5rem] flex items-center justify-center transition-colors group-hover:border-white/[0.1]">
              {item.mock}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideMarketSize() {
  const cards: {
    label: string
    stat: string
    bullets: string[]
    highlight?: string
    color: string
    dotColor: string
    sourceLabel?: string
    sourceUrl?: string
  }[] = [
    {
      label: 'TAM — Professional Independent Music Infrastructure',
      stat: '$700M – $1.2B',
      bullets: [
        '11M+ artists globally on Spotify; independents ~50% of recorded music revenue (IFPI)',
        '200K–500K revenue-generating independent artists (Spotify Loud & Clear, MIDiA)',
        'Professional artists use 4–8 SaaS tools; ~$1,000–$1,500/year stack spend',
      ],
      highlight: '600K serious operators × ~$1,200/yr → ~$720M; upper range ~$1.2B',
      color: 'border-[#FF2300]/25 bg-[#FF2300]/5',
      dotColor: 'bg-[#FF2300]',
      sourceLabel: 'IFPI, Spotify Loud & Clear, MIDiA Research',
    },
    {
      label: 'SAM — English-Speaking, Team-Operated Artists',
      stat: '$300M – $480M',
      bullets: [
        '250K–400K professional artists in US, UK, Canada, Australia, EU English markets',
        'Structured teams: managers, collaborators, marketing workflows',
        'Early adopters of workflow software',
      ],
      highlight: '250K–400K × ~$1,200/year',
      color: 'border-violet-500/25 bg-violet-500/5',
      dotColor: 'bg-violet-500',
      sourceLabel: undefined,
    },
    {
      label: 'SOM — Category Leader in Music Vertical (5-Year Target)',
      stat: '$15M – $20M ARR',
      bullets: [
        '8,000–15,000 paying accounts',
        'Blended ARPU ~$100/month',
        'Focus: managed & team-operated artists',
      ],
      color: 'border-emerald-500/25 bg-emerald-500/5',
      dotColor: 'bg-emerald-500',
      sourceLabel: 'Internal estimate',
    },
  ]
  return (
    <div>
      <SlideTag>Market Size</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
        A large, underserved market <span className="text-[#FF2300]">that keeps growing.</span>
      </h2>
      <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {cards.map((s, i) => (
          <div key={i} className={`rounded-xl border ${s.color} p-4 sm:p-5 transition-colors hover:border-opacity-40`}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{s.label.split(' — ')[0]}</h3>
            <p className="text-xl sm:text-2xl font-bold text-white mt-0.5">{s.stat}</p>
            <p className="text-[12px] text-zinc-400 mt-0.5 font-medium">{s.label.split(' — ')[1]}</p>
            <ul className="mt-3 space-y-1.5">
              {s.bullets.map((bullet, j) => (
                <li key={j} className="flex items-start gap-2 text-[13px] text-zinc-300 leading-snug">
                  <span className={`h-1 w-1 rounded-full ${s.dotColor} shrink-0 mt-1.5`} aria-hidden />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            {s.highlight && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs font-medium text-white flex items-center gap-1.5">
                  <span className="text-zinc-500">→</span> {s.highlight}
                </p>
              </div>
            )}
            {s.sourceLabel && (
              <p className="mt-3 pt-2 border-t border-white/5 text-[10px] text-zinc-500">Source: {s.sourceLabel}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideBusinessModel() {
  const tiers = [
    {
      tier: 'Artist',
      audienceLabel: 'Independent artist',
      price: '$45',
      note: '/mo',
      annualPrice: '$459',
      annualNote: '/yr',
      annualMonthly: '$38.25',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: undefined as string[] | undefined,
      popular: false,
    },
    {
      tier: 'Artist Pro',
      audienceLabel: 'Industry professionals',
      price: '$75',
      note: '/mo',
      annualPrice: '$765',
      annualNote: '/yr',
      annualMonthly: '$63.75',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: ['Ticket Sales Tracker', 'Tour Manager', 'Dizee AI Assistant', 'Finance Tracking'],
      popular: true,
    },
    {
      tier: 'Enterprise',
      audienceLabel: 'Industry professionals',
      price: '$125',
      note: '/mo',
      annualPrice: '$1,275',
      annualNote: '/yr',
      annualMonthly: '$106.25',
      audience: '2+ users ($15 per additional seat)',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync', 'Tour Manager', 'Finance Tracking', 'Ticket Sales Tracker', 'Dizee AI Assistant'],
      plusFeatures: ['Teams', 'Songwriter Credits', 'Dedicated Customer Support'],
      popular: false,
    },
  ]
  return (
    <div>
      <SlideTag>Business Model</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>
      <div className="mt-10 flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
        {tiers.map((p, i) => (
          <div key={i} className="relative flex-1 flex flex-col min-h-0">
            {i === 1 && (
              <p className="text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest mb-3">For industry professionals</p>
            )}
            {i === 0 && (
              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">For independent artists</p>
            )}
            {i === 2 && <p className="text-[11px] font-semibold uppercase tracking-widest mb-3 invisible" aria-hidden>For industry professionals</p>}
            <div className={`flex-1 flex flex-col min-h-0 rounded-xl border-2 p-5 ${(i === 1 || i === 2) ? 'border-[#FF2300]/40 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20' : 'border-white/[0.06] bg-white/[0.02]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">{p.tier}</h3>
                {p.popular && (
                  <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">Popular</span>
                )}
              </div>
              {p.audienceLabel && <p className="text-base text-zinc-500 mb-2">{p.audienceLabel}</p>}
              <p className="text-2xl font-bold text-white mt-1">{p.price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{p.note}</span></p>
              <p className="text-[13px] text-zinc-500 mt-0.5">Save 15% annually: <span className="text-zinc-400">{p.annualPrice}</span><span className="text-[11px] text-zinc-600 ml-0.5">{p.annualNote}</span> <span className="text-zinc-500">({p.annualMonthly}/mo)</span></p>
              <p className="text-base text-[#FF2300] font-medium mt-1">{p.audience}</p>
              <ul className="mt-4 space-y-1.5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    <span className="text-base text-zinc-400">{f}</span>
                  </li>
                ))}
              </ul>
              {p.plusFeatures && p.plusFeatures.length > 0 && (
                <>
                  <div className="my-3 border-t border-white/[0.06]" />
                  <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Plus</p>
                  <ul className="space-y-1.5">
                    {p.plusFeatures.map((f, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-[#FF2300] shrink-0" />
                        <span className="text-base text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideGTM() {
  const phases = [
    { phase: 'Phase 1', title: 'Founder-Led Wedge', timeline: 'Months 0–6', target: '0 → 40 accounts', items: ['Direct outreach to in-network managers', '1:1 founder-led onboarding', 'Early product feedback loop'] },
    { phase: 'Phase 2', title: 'Signal Amplification', timeline: 'Months 6–12', target: '40 → 200 accounts', items: ['Case studies', 'Referral incentives', 'Partnerships', 'Testimonials'] },
    { phase: 'Phase 3', title: 'Scalable Acquisition', timeline: 'Months 12–24', target: '200 → 1,200+ paying customers', items: ['Content + education', 'Workflow partnerships', 'Referral engine', 'API integrations'] },
  ]
  return (
    <div>
      <SlideTag>Go-To-Market</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Go to market strategy.
      </h2>
      <div className="mt-12 flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-2">
        {phases.map((p, i) => (
          <div key={i} className="flex items-stretch gap-2 flex-1">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 flex-1">
              <span className="text-base sm:text-lg font-bold text-[#FF2300]">{p.phase}</span>
              <h3 className="text-[16px] font-semibold text-white mt-2">{p.title}</h3>
              <div className="mt-3 space-y-1">
                <p className="text-sm text-zinc-500">{p.timeline}</p>
                <p className="text-base sm:text-lg font-semibold text-zinc-200">{p.target}</p>
              </div>
              <ul className="mt-4 space-y-2">
                {p.items.map((item, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />
                    <span className="text-base text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {i < phases.length - 1 && (
              <div className="hidden lg:flex items-center justify-center flex-shrink-0 px-1">
                <ArrowRight className="w-6 h-6 text-[#FF2300]/50" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideCompetition() {
  /* Buckets: (1) Static software cluster (Notion/Asana style) — left/lower.
     (2) Music tools/distribution (Venice, IndieFlow) — Venice = static + light AI copilot, left of DIZEE; not 360.
     (3) DIZEE in its own lane — Intelligent + 360. Artist Growth: closer to DIZEE (claims intelligence) but not overlapping.
     Master Tour/Mastercore: not intelligent, not 360 — lower/left, slightly more connected than pure static. */
  const competitors: { name: string; x: number; y: number; highlight?: boolean }[] = [
    /* Static software cluster */
    { name: 'Slack', x: 18, y: 38 },
    { name: 'Monday', x: 24, y: 44 },
    { name: 'Asana', x: 32, y: 54 },
    { name: 'Notion', x: 40, y: 58 },
    { name: 'Master Tour', x: 36, y: 15 },   // Down: not intelligent, not 360
    /* Music tools — Venice higher; Artist Growth down and right */
    { name: 'IndieFlow', x: 48, y: 36 },
    { name: 'Venice Music', x: 28, y: 78 },  // Moved right
    { name: 'Artist Growth', x: 66, y: 64 }, // Down and right
    { name: 'DIZEE', x: 90, y: 92, highlight: true },
  ]
  return (
    <div>
      <SlideTag>Competition</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        From static software to an{' '}
        <span className="text-[#FF2300]">intelligent operator.</span>
      </h2>
      <div className="mt-10 relative w-full max-w-[700px] mx-auto">
        <svg viewBox="0 0 600 520" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
          <line x1="300" y1="40" x2="300" y2="500" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <line x1="20" y1="270" x2="580" y2="270" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <text x="20" y="265" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600"><tspan fill="#FF2300">X: </tspan><tspan fill="white">Static Software</tspan></text>
          <text x="580" y="265" textAnchor="end" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">360° Career Management</text>
          <text x="300" y="28" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">Y: Intelligent Operating System</text>
          <text x="300" y="512" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">Disconnected Workflows</text>
          {competitors.map((c, i) => {
            const cx = 20 + (c.x / 100) * 560
            const cy = 500 - (c.y / 100) * 460
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r={c.highlight ? 14 : 8} fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.8)'} />
                <text x={cx} y={cy + (c.highlight ? 28 : 22)} textAnchor="middle" fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.6)'} fontSize={c.highlight ? '14' : '12'} fontWeight={c.highlight ? '700' : '600'}>{c.name}</text>
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

function SlideAdvantages() {
  const bullets = [
    'Built by a manager who\'s led artists to 2 Grammy nominations and responsible for 3B+ streams.',
    'CEO with proven industry credibility and owns an active Billboard-charting record label.',
    'Deep first-hand understanding of the full artist career lifecycle.',
    'Rare hybrid team of music insiders and modern product builders.',
    'CTO and engineering team have built diverse technology for iconic artists and across the industry — the right builders for this product.',
    'Experienced founders with growth-venture operating and capital-raising experience.',
    <span key="ai"><DizeeWord /> AI knowledge base informed by 15+ years of lived industry experience and real career data.</span>,
    'Strong global relationships across artists, agencies, promoters, and labels.',
    'Capital-efficient, agile, and lean operating team.',
    'Based in Los Angeles, the center of global music culture.',
  ]
  return (
    <TwoCol
      tagNode={<>Why <DizeeWord /> Wins</>}
      headlineNode={<>Why <DizeeWord className="text-[#FF2300]" /> wins.</>}
      right={
        <div className="flex flex-col items-center gap-4 w-full max-w-lg">
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
            <img
              src="/deck/party.png"
              alt="Why DIZEE wins"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      }
    >
      <ul className="mt-8 space-y-4 max-w-3xl">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-base text-zinc-300">{b}</span>
          </li>
        ))}
      </ul>
    </TwoCol>
  )
}

const TEAM_MEMBERS = [
  {
    name: 'Ryan Thomas',
    role: 'CEO',
    image: '/deck/ryan.png',
    points: [
      '15+ years in the dance music industry',
      'Managed Grammy-nominated and platinum-selling artists',
      'Deep global relationships with agencies, promoters, and labels',
    ],
  },
  {
    name: 'Edward Contreras',
    role: 'CTO',
    image: '/deck/edward.png',
      points: [
      'Technologist, product builder, and entrepreneur',
      'Expertise in AI systems, music technology, and multi-product ecosystems',
      <>Architect behind <DizeeWord />'s platform and integration layer</>,
    ],
  },
  {
    name: 'Aaron P. Wong',
    role: 'COO',
    image: '/deck/aaron.png',
    points: [
      'Founder of Super73, a globally recognized electric bike company',
      'Experienced in scaling hardware businesses and social presence',
      'Raised and closed multiple rounds of venture funding',
    ],
  },
  {
    name: 'Owais Ghaus',
    role: 'CMO',
    image: '/deck/owais.png',
    points: [
      'Artist, marketer, and technologist',
      'Music executive with deep industry connections',
      'Specializes in content strategy, creator marketing, and artist development',
    ],
  },
  {
    name: 'Christopher Alba',
    role: 'CBO',
    image: '/deck/christopher.png',
    points: [
      'Pioneer in the dance music industry with deep roots in creative direction and brand strategy',
      'Creative director for hundreds of artists across the music industry',
      'Chief Brand Officer — leads creative direction, brand identity, and visual storytelling for DIZEE',
    ],
  },
]
function TeamCard({ t }: { t: (typeof TEAM_MEMBERS)[0] }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7 flex flex-col min-h-[220px]">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border border-white/[0.08] flex-shrink-0 bg-white/5">
          <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] sm:text-[16px] font-semibold text-white tracking-tight">{t.name}</h3>
          <p className="text-[12px] text-[#FF2300] font-medium mt-0.5">{t.role}</p>
        </div>
      </div>
      <ul className="space-y-3 flex-1">
        {t.points.map((point, j) => (
          <li key={j} className="flex items-start gap-2">
            <span className="mt-[0.55em] h-1.5 w-1.5 rounded-full bg-zinc-500 shrink-0" aria-hidden />
            <p className="text-[15px] text-zinc-400 leading-relaxed flex-1 min-w-0 pt-0">{point}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
function SlideTeam() {
  const firstRow = TEAM_MEMBERS.slice(0, 3)
  const secondRow = TEAM_MEMBERS.slice(3, 5) // Owais, Christopher
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {firstRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:max-w-[66.666%] mx-auto">
        {secondRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
    </div>
  )
}

function SlideAsk() {
  const feedItems = [
    'Product-market fit with real artists and managers',
    'Core platform development and system hardening',
    'Deep integrations across the music ecosystem',
    'Onboarding marquee artists and management partners',
    'Founding team focus and execution',
    'Secure, scalable infrastructure and compliance',
  ]
  const centerLabel = 'Beta expansion and early adoption'
  return (
    <div>
      <SlideTag>The Ask</SlideTag>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-baseline sm:gap-4 gap-2">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tighter text-white leading-tight">
          Raising{' '}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#FF2300]">$2.5M</span>
            <span className="absolute inset-0 bg-[#FF2300]/15 blur-xl rounded-full -z-0" aria-hidden />
          </span>
          {' '}to accelerate.
        </h2>
        <p className="text-lg sm:text-xl text-zinc-400 font-medium shrink-0">Pre-seed · Use of funds</p>
      </div>
      {/* Six points that feed into the center */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {feedItems.map((label, i) => (
          <div
            key={i}
            className="group flex items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.06] px-4 py-3 sm:px-5 sm:py-4 transition-colors"
          >
            <span className="text-[#FF2300] shrink-0 mt-0.5 font-bold opacity-80" aria-hidden>→</span>
            <p className="text-[14px] sm:text-[15px] text-zinc-300 leading-snug">{label}</p>
          </div>
        ))}
      </div>
      {/* Connector: "all feed into" */}
      <div className="mt-4 flex flex-col items-center gap-1">
        <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <span className="text-xs sm:text-sm font-medium text-zinc-500 uppercase tracking-wider">All driving toward</span>
        <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
      {/* Center outcome */}
      <div className="mt-4 mx-auto max-w-2xl rounded-2xl border-2 border-[#FF2300]/40 bg-[#FF2300]/10 px-6 py-5 sm:px-8 sm:py-6 text-center shadow-[0_0_40px_-8px_rgba(255,35,0,0.25)]">
        <span className="h-3 w-3 rounded-full bg-[#FF2300] inline-block mb-3 ring-4 ring-[#FF2300]/30" aria-hidden />
        <p className="text-lg sm:text-xl text-white font-semibold leading-snug">{centerLabel}</p>
      </div>
    </div>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The new operating system and source of truth for modern music careers.
      </p>
      <div className="mt-12 space-y-2">
        <p className="text-[15px] sm:text-[16px] text-[#FF2300] font-medium">invest@dizee.com</p>
        <p className="text-base sm:text-[17px] text-white">Pre-seed February 2026</p>
        <p className="text-base sm:text-[17px] text-white">dizee.com</p>
      </div>
    </div>
  )
}

/* Version B: end-cap slide with only the closing line (optional — remove from SLIDES if using Version A only). */
function SlideEndCap() {
  return (
    <div className="flex flex-col justify-center items-center text-center py-8">
      <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-snug max-w-4xl">
        <DizeeWord /> is building the modern operating system the music industry has been missing.
      </p>
      <p className="mt-8 text-lg sm:text-xl text-zinc-400 font-medium max-w-2xl">
        The team is assembled. The product is in motion.{' '}
        <span className="text-[#FF2300]">Timing is now.</span>
      </p>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: "What's Happening Now", component: SlideWhatsHappening },
  { title: 'Industry Trends', component: SlideIndustryTrends },
  { title: 'Current State', component: SlideProblemCurrentTools },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Real Conversations', component: SlideExampleMessages },
  { title: 'Solution', component: SlideCentralizes },
  { title: 'Product', component: SlideProduct },
  { title: 'Product Breakdown', component: SlideProductBreakdown },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Why DIZEE Wins', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing Line', component: SlideEndCap },
  { title: 'Closing', component: SlideClosing },
]
